import {
  Combobox,
  Field,
  Spinner,
} from '@noorden-insurtech/alba-design-system';
import { X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useAuditActorOptions, type AuditActorOption } from '../hooks';

interface AuditActorComboboxProps {
  /** `sub` selecionado (é o que vai para a URL e para a API). */
  value: string;
  /** Rótulo junto, para a tela citar o autor pelo nome no aviso do recorte. */
  onChange: (_actorSub: string, _label?: string) => void;
  /**
   * Nome do autor que a tela já conhece — vindo do atalho "Ver ações deste
   * autor" ou das linhas carregadas. Sem ele, um `sub` aberto pela URL
   * apareceria cru no campo.
   */
  knownLabel?: string;
}

/**
 * Autor do recorte "Por autor": busca por nome ou e-mail e aceita o ID do
 * usuário colado direto.
 */
export function AuditActorCombobox({
  value,
  onChange,
  knownLabel,
}: AuditActorComboboxProps) {
  const [search, setSearch] = useState('');
  const { options, isLoading, needsMoreChars } = useAuditActorOptions(search);

  // `sub → rótulo` que sobrevive às buscas: ao selecionar, o Ark escreve o
  // rótulo no input, a busca muda e o item some de `options`.
  const [labelByValue, setLabelByValue] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!options.length) return;
    setLabelByValue(prev => {
      let changed = false;
      const next = { ...prev };
      for (const option of options) {
        // O rótulo de um id colado é o próprio id: deixa o nome real, que
        // chega pelas linhas carregadas, ocupar o campo.
        if (option.isRawId) continue;
        if (next[option.value] !== option.label) {
          next[option.value] = option.label;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [options]);

  const selectedLabel = value
    ? (labelByValue[value] ?? knownLabel ?? value)
    : undefined;

  // A coleção precisa conhecer o item selecionado mesmo quando a busca atual
  // não o devolve, senão o Ark perde o rótulo ao fechar o popup.
  const collection: AuditActorOption[] = useMemo(() => {
    if (!value || options.some(option => option.value === value)) {
      return options;
    }
    return [{ value, label: selectedLabel ?? value }, ...options];
  }, [options, value, selectedLabel]);

  return (
    <Field.Root className='w-full md:max-w-[420px]'>
      <Field.Label>Autor</Field.Label>
      <Combobox.Root
        items={collection}
        value={value ? [value] : []}
        onValueChange={details => {
          const actorSub = details.value[0] || '';
          const picked = collection.find(option => option.value === actorSub);
          onChange(actorSub, picked?.isRawId ? undefined : picked?.label);
        }}
        onInputValueChange={details => setSearch(details.inputValue)}
        selectionBehavior='replace'
      >
        <Combobox.Control className='relative'>
          <Combobox.Trigger>
            {/* Espaço à direita para o "limpar" não cobrir o nome longo. */}
            <span className={`block truncate ${value ? 'pr-7' : ''}`}>
              {selectedLabel ?? 'Selecione o autor'}
            </span>
          </Combobox.Trigger>
          {value && (
            <Combobox.ClearTrigger
              type='button'
              className='absolute right-9 top-1/2 z-10 flex -translate-y-1/2 items-center justify-center rounded p-1 text-alba-gray-600 hover:bg-alba-gray-200 hover:text-alba-black-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-alba-blue-500'
              aria-label='Limpar autor'
              title='Limpar autor'
            >
              <X className='h-4 w-4' strokeWidth={1.6} />
            </Combobox.ClearTrigger>
          )}
        </Combobox.Control>
        <Combobox.Positioner>
          <Combobox.Content>
            <Combobox.Input
              placeholder='Nome, e-mail ou ID do usuário...'
              autoFocus
              onKeyDown={event => event.stopPropagation()}
            />
            {isLoading ? (
              <div className='flex items-center justify-center py-8'>
                <Spinner size='sm' colorScheme='blue' />
              </div>
            ) : needsMoreChars ? (
              <p className='px-3 py-4 text-sm text-alba-gray-600'>
                Digite ao menos 2 letras do nome ou do e-mail.
              </p>
            ) : (
              <Combobox.List>
                <Combobox.ItemGroup>
                  <Combobox.ItemGroupLabel>Usuários</Combobox.ItemGroupLabel>
                  {options.map(option => (
                    <Combobox.Item key={option.value} item={option}>
                      <Combobox.ItemText>{option.label}</Combobox.ItemText>
                      <Combobox.ItemIndicator />
                    </Combobox.Item>
                  ))}
                </Combobox.ItemGroup>
                {!options.length && <Combobox.Empty />}
              </Combobox.List>
            )}
          </Combobox.Content>
        </Combobox.Positioner>
      </Combobox.Root>
      <Field.HelperText>
        Busque pelo nome ou e-mail, ou cole o ID do usuário.
      </Field.HelperText>
    </Field.Root>
  );
}
